/**
 * Diff a device's Specs form back to a minimal `PATCH /api/assets/:id` body.
 *
 * Issue #1024: the device page rendered every spec as read-only text, so an
 * import artifact like LOCATION ON PROPERTY = "Information Contact Information"
 * could not be corrected from anywhere in the app — even though the PATCH route
 * has accepted all of these fields the whole time.
 *
 * Send-only-what-changed is deliberate, for two reasons:
 *   1. A correction touches one field. Re-sending the other twelve invites a
 *      clobber if a tech edited the device from the field mid-form.
 *   2. Two of these enums are narrower on the PATCH route than the values that
 *      already exist in the data (legacy `dcva` assemblies, the wider
 *      `domestic_irrigation`/`domestic_fire` serves values from the form-engine
 *      registry). Leaving an untouched legacy value out of the body means it
 *      survives instead of failing validation on an unrelated edit.
 *
 * Empty text clears the column (`null`); the server treats these fields as
 * nullable. `name` is the one exception — it is NOT NULL, so a blank name is
 * dropped rather than sent, and the caller blocks Save on it.
 */

export interface AssetSpecsFormValues {
    name: string;
    assemblyType: string;
    sizeLabel: string;
    manufacturer: string;
    model: string;
    serialNumber: string;
    meterNumber: string;
    serviceType: string;
    serviceTypeOther: string;
    locationDescription: string;
    notes: string;
    isFireline: boolean;
    isDetectorAssembly: boolean;
    /** Tri-state, because "we don't know" is a real answer: "" | "yes" | "no". */
    inVault: string;
    /** "" = not known | inside_above_ground | inside_below_ground | outside. */
    installSetting: string;
}

export interface AssetSpecsPatchBody {
    name?: string;
    assemblyType?: string | null;
    sizeLabel?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    serialNumber?: string | null;
    meterNumber?: string | null;
    serviceType?: string | null;
    serviceTypeOther?: string | null;
    locationDescription?: string | null;
    notes?: string | null;
    isFireline?: boolean;
    isDetectorAssembly?: boolean;
    inVault?: boolean | null;
    installSetting?: InstallSetting | null;
}

export type InstallSetting = "inside_above_ground" | "inside_below_ground" | "outside";

/** The choices on the Specs form, in plain words. */
export const INSTALL_SETTING_OPTIONS: Array<{ value: InstallSetting; label: string }> = [
    { value: "inside_above_ground", label: "Inside, above ground" },
    { value: "inside_below_ground", label: "Inside, below ground" },
    { value: "outside", label: "Outside" },
];

/** The asset fields the Specs form reads from. */
export interface AssetSpecsSource {
    name: string;
    assembly_type: string | null;
    size_label: string | null;
    manufacturer: string | null;
    model: string | null;
    serial_number: string | null;
    meter_number: string | null;
    service_type: string | null;
    service_type_other: string | null;
    location_description: string | null;
    notes: string | null;
    is_fireline: boolean;
    is_detector_assembly: boolean;
    in_vault: boolean | null;
    install_setting?: string | null;
}

/** Seed the form from the asset row — nulls become empty strings. */
export function assetSpecsFormValues(a: AssetSpecsSource): AssetSpecsFormValues {
    return {
        name: a.name ?? "",
        assemblyType: a.assembly_type ?? "",
        sizeLabel: a.size_label ?? "",
        manufacturer: a.manufacturer ?? "",
        model: a.model ?? "",
        serialNumber: a.serial_number ?? "",
        meterNumber: a.meter_number ?? "",
        serviceType: a.service_type ?? "",
        serviceTypeOther: a.service_type_other ?? "",
        locationDescription: a.location_description ?? "",
        notes: a.notes ?? "",
        isFireline: !!a.is_fireline,
        isDetectorAssembly: !!a.is_detector_assembly,
        inVault: a.in_vault === null || a.in_vault === undefined ? "" : a.in_vault ? "yes" : "no",
        installSetting: a.install_setting ?? "",
    };
}

function nullableText(next: string, prev: string | null): { changed: boolean; value: string | null } {
    const trimmed = next.trim();
    const before = (prev ?? "").trim();
    return { changed: trimmed !== before, value: trimmed === "" ? null : trimmed };
}

export function buildAssetSpecsPatch(original: AssetSpecsSource, draft: AssetSpecsFormValues): AssetSpecsPatchBody {
    const body: AssetSpecsPatchBody = {};

    const name = draft.name.trim();
    if (name !== "" && name !== (original.name ?? "").trim()) body.name = name;

    const textFields: Array<[keyof AssetSpecsPatchBody, string, string | null]> = [
        ["assemblyType", draft.assemblyType, original.assembly_type],
        ["sizeLabel", draft.sizeLabel, original.size_label],
        ["manufacturer", draft.manufacturer, original.manufacturer],
        ["model", draft.model, original.model],
        ["serialNumber", draft.serialNumber, original.serial_number],
        ["meterNumber", draft.meterNumber, original.meter_number],
        ["serviceType", draft.serviceType, original.service_type],
        ["serviceTypeOther", draft.serviceTypeOther, original.service_type_other],
        ["locationDescription", draft.locationDescription, original.location_description],
        ["notes", draft.notes, original.notes],
    ];
    for (const [key, next, prev] of textFields) {
        const { changed, value } = nullableText(next, prev);
        if (changed) (body as Record<string, unknown>)[key] = value;
    }

    if (draft.isFireline !== !!original.is_fireline) body.isFireline = draft.isFireline;
    if (draft.isDetectorAssembly !== !!original.is_detector_assembly) {
        body.isDetectorAssembly = draft.isDetectorAssembly;
    }

    const nextVault = draft.inVault === "" ? null : draft.inVault === "yes";
    const prevVault = original.in_vault === undefined ? null : original.in_vault;
    if (nextVault !== prevVault) body.inVault = nextVault;

    const nextSetting = (draft.installSetting || null) as InstallSetting | null;
    if (nextSetting !== (original.install_setting ?? null)) body.installSetting = nextSetting;

    return body;
}

/** True when there is nothing to send — the caller can skip the round-trip. */
export function isEmptyPatch(body: AssetSpecsPatchBody): boolean {
    return Object.keys(body).length === 0;
}
